import moment from "moment-timezone";
import Config from "../models/config";
import { UploadDayStatus } from "../models/workingTimeUploadDay";

const DAY_KEY_FORMAT = "YYYY-MM-DD";
const DAY_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Ngày theo giờ Việt Nam dạng "YYYY-MM-DD". Chuỗi đã là day key thì giữ nguyên. */
export const toDayKey = (date: Date | string | number): string => {
    if (typeof date === "string" && DAY_KEY_REGEX.test(date)) return date;
    return moment(date).tz(Config.TIMEZONE).format(DAY_KEY_FORMAT);
};

export const isDayKey = (value: unknown): value is string =>
    typeof value === "string" && DAY_KEY_REGEX.test(value) && moment(value, DAY_KEY_FORMAT, true).isValid();

/** Day key -> Date 00:00 +07:00 (chuẩn lưu DB của working_time). */
export const dayKeyToDate = (key: string): Date => moment.tz(key, DAY_KEY_FORMAT, Config.TIMEZONE).toDate();

export const listDayKeys = (startKey: string, endKey: string): string[] => {
    const result: string[] = [];
    const cursor = moment.tz(startKey, DAY_KEY_FORMAT, Config.TIMEZONE);
    const end = moment.tz(endKey, DAY_KEY_FORMAT, Config.TIMEZONE);
    while (cursor.isSameOrBefore(end, "day")) {
        result.push(cursor.format(DAY_KEY_FORMAT));
        cursor.add(1, "day");
    }
    return result;
};

/** Khoảng ngày [nhỏ nhất, lớn nhất] của các dòng trong file; null nếu không có dòng nào. */
export const getRowsDayRange = (rows: { date: Date | string }[]): { start: string; end: string } | null => {
    if (!rows.length) return null;
    const keys = rows.map((row) => toDayKey(row.date)).sort();
    return { start: keys[0], end: keys[keys.length - 1] };
};

export interface IPartitionResult<T> {
    effectiveEnd: string;
    inRange: T[];
    ignored: T[];
    dayCounts: Record<string, number>;
    loadedDays: string[];
    emptyDays: string[];
}

/**
 * Tách dòng chấm công theo khoảng [startKey, min(endKey, todayKey)].
 * loadedDays: ngày trong khoảng có >= 1 dòng; emptyDays: ngày trong khoảng không có dòng nào.
 */
export const partitionRowsByRange = <T extends { date: Date | string }>(
    rows: T[],
    startKey: string,
    endKey: string,
    todayKey: string
): IPartitionResult<T> => {
    const effectiveEnd = endKey < todayKey ? endKey : todayKey;
    const inRange: T[] = [];
    const ignored: T[] = [];
    const dayCounts: Record<string, number> = {};
    rows.forEach((row) => {
        const key = toDayKey(row.date);
        if (key >= startKey && key <= effectiveEnd) {
            inRange.push(row);
            dayCounts[key] = (dayCounts[key] ?? 0) + 1;
        } else {
            ignored.push(row);
        }
    });
    const days = startKey <= effectiveEnd ? listDayKeys(startKey, effectiveEnd) : [];
    return {
        effectiveEnd,
        inRange,
        ignored,
        dayCounts,
        loadedDays: days.filter((d) => !!dayCounts[d]),
        emptyDays: days.filter((d) => !dayCounts[d]),
    };
};

export type CalendarDayStatus = UploadDayStatus | "missing" | "off" | "future";

/** Thứ 7, CN hoặc ngày lễ là nghỉ; ngày làm bù luôn là ngày làm việc. */
export const isOffDay = (dayKey: string, holidays: string[], complementDays: string[]): boolean => {
    if (complementDays.includes(dayKey)) return false;
    if (holidays.includes(dayKey)) return true;
    return moment.tz(dayKey, DAY_KEY_FORMAT, Config.TIMEZONE).isoWeekday() >= 6;
};

export const getUploadDayStatus = (
    dayKey: string,
    marker: { status: UploadDayStatus } | undefined,
    holidays: string[],
    complementDays: string[],
    todayKey: string
): CalendarDayStatus => {
    if (dayKey > todayKey) return "future";
    if (marker) return marker.status;
    if (isOffDay(dayKey, holidays, complementDays)) return "off";
    return "missing";
};

export type AttendanceCellStatus = "ok" | "late" | "missingPunch" | "leave" | "off" | "noData" | "future";

const MORNING_SLOTS = [Config.MORNING, Config.MORNING_VNESE];
const AFTERNOON_SLOTS = [Config.AFTERNOON, Config.AFTERNOON_VNESE];
const ALL_DAY_SLOTS = [Config.ALL_DAY, Config.ALL_DAY_VNESE];

const toMinutes = (time: string) => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
};

/** Trạng thái 1 ô (nhân viên × ngày) của lưới chấm công chi tiết. */
export const classifyAttendanceCell = ({
    record,
    leaveSlots,
    isOff,
    isFuture,
}: {
    record?: { checkin: string; checkout: string };
    leaveSlots: string[];
    isOff: boolean;
    isFuture: boolean;
}): AttendanceCellStatus => {
    if (isFuture) return "future";
    const leaveMorning = leaveSlots.some((s) => MORNING_SLOTS.includes(s) || ALL_DAY_SLOTS.includes(s));
    const leaveAfternoon = leaveSlots.some((s) => AFTERNOON_SLOTS.includes(s) || ALL_DAY_SLOTS.includes(s));
    if (leaveMorning && leaveAfternoon) return "leave";
    if (!record) return isOff ? "off" : "noData";
    if (!record.checkin || !record.checkout) return "missingPunch";
    const threshold = leaveMorning ? Config.LATE.timeInAfternoon : Config.LATE.timeInMorning;
    return toMinutes(record.checkin) > threshold ? "late" : "ok";
};
